import { supportsSessionResume, type Identity } from '@cef-ai/wallet-identity';
import type { HostToPopup, PopupToHost, SessionStatusResult } from '@cef-ai/wallet';

/**
 * Fail-open bound on the `bootResume` gate below (issue #36).
 *
 * `ApiClient` puts no timeout/`AbortSignal` on its `fetch` calls, so a
 * `session.refresh()` against a black-holed connection can stay pending
 * indefinitely. Without a bound, this handler would never reply and the host's
 * `checkSessionStatus()` would sit on its own transport timeout before falling
 * back to a ceremony — strictly worse than the pre-fix behavior.
 *
 * With the bound, a pathologically slow refresh degrades to EXACTLY the pre-fix
 * behavior (answer from whatever `identity.isAuthenticated` says right now)
 * rather than hanging. This is a safety valve, not the mechanism: the mechanism
 * is deterministic (await the actual promise), and this only ever fires when the
 * network has already failed in a way nothing here can fix.
 */
const BOOT_RESUME_WAIT_MS = 10_000;

export interface SessionStatusHandlerDeps {
  identity: Identity;
  send: (msg: PopupToHost) => void;
  /**
   * Issue #36. The surface's boot-time silent-resume attempt, hoisted out of
   * `EmbedWalletRoute`'s effect so this handler can await it before answering.
   *
   * `wallet:ready` is announced when the bridge's `message` listener attaches
   * (`PopupHostBridge.start`), NOT when the session is established — and the
   * host's `IframeTransport` flushes its outbox on exactly that signal. So a
   * host that calls `resume()`/`login()` from a boot effect gets its
   * `wallet:session:status` delivered while `apiClient.session.refresh()` (a
   * network round trip) is still in flight. Answering immediately from
   * `identity.isAuthenticated` was truthful but premature — `authenticated:
   * false` — so resume-first `login()` fell through to a passkey popup on every
   * page refresh.
   *
   * Resolves (never rejects — a failed refresh is a legitimate "stay logged
   * out" outcome) once the boot resume has settled either way. Optional:
   * omitted by thin unit-test doubles and by any caller with no boot resume of
   * its own, in which case this handler answers immediately, exactly as before.
   */
  bootResume?: Promise<unknown>;
  /** Override for {@link BOOT_RESUME_WAIT_MS}. Tests only. */
  bootResumeTimeoutMs?: number;
}

/**
 * Task 4. Answers `wallet:session:status` from the concrete `identity` this
 * bridge holds — WITHOUT running any WebAuthn ceremony. This is what lets
 * `EmbedWallet.login()` resume a bearer-active (Task 2/3) or fully keyed
 * session silently instead of always popping the ceremony.
 *
 * Issue #36: it first awaits `deps.bootResume` (bounded — see
 * {@link BOOT_RESUME_WAIT_MS}) so a request that arrives mid-boot gets the
 * settled answer rather than a premature one.
 *
 * This PRESERVES the "`wallet:session:status` never triggers WebAuthn"
 * invariant (`ARCHITECTURE.md` Key invariants) rather than bending it. The
 * awaited promise is an already-in-flight `apiClient.session.refresh()` — a
 * plain credentialed POST that reads the HttpOnly refresh cookie and touches no
 * authenticator. This handler starts nothing; it only declines to answer with a
 * value it knows is still settling, then reads the same two fields it always
 * read. There is still no code path from this message to a ceremony.
 *
 * `hasKeys()` is off the core `Identity` interface (see
 * `SessionResume`/`supportsSessionResume` in `@cef-ai/wallet-identity`) —
 * narrow via `supportsSessionResume` rather than widening the handler's dep
 * type, mirroring the boot-time resume wiring in `EmbedWallet.tsx`. Thin test
 * doubles that don't implement it are treated as never having keys.
 *
 * Task 20: the reply also carries `accountId`/`email`/`displayName` (when
 * authenticated and `identity.account` has resolved one) — read straight off
 * that already-resident public field, same no-I/O, no-ceremony read as
 * `addresses`/`credentialId` above. This does NOT add anything else this
 * handler awaits: only `bootResume` is ever awaited here.
 */
export function createSessionStatusHandler(deps: SessionStatusHandlerDeps) {
  return async (
    msg: Extract<HostToPopup, { type: 'wallet:session:status' }>,
    _meta: { origin: string },
  ): Promise<void> => {
    if (deps.bootResume) await awaitBootResume(deps.bootResume, deps.bootResumeTimeoutMs ?? BOOT_RESUME_WAIT_MS);
    const authenticated = deps.identity.isAuthenticated;
    const hasKeys = supportsSessionResume(deps.identity) ? deps.identity.hasKeys() : false;
    // Task 20. `identity.account` is already-resident public state (set by
    // register/login/resumeSession from the server's session response) — this
    // is a plain field read, not I/O, so it does not widen what this handler
    // awaits. `account` is `readonly account?` on the `Identity` interface (thin
    // test doubles may omit it entirely), so read it defensively rather than
    // assuming `IdentityImpl`'s always-defined field. Follows the existing
    // addresses/credentialId shape: reported only when authenticated AND an
    // account actually resolved — an unauthenticated reply carries none of
    // these fields at all, same as it carries no addresses/credentialId.
    const account = authenticated ? deps.identity.account : undefined;
    const result: SessionStatusResult = {
      authenticated,
      hasKeys,
      ...(authenticated
        ? { addresses: deps.identity.addresses ?? undefined, credentialId: deps.identity.credentialId ?? undefined }
        : {}),
      ...(account ? { accountId: account.accountId, email: account.email, displayName: account.displayName } : {}),
    };
    deps.send({ type: 'wallet:result', id: msg.id, result });
  };
}

/**
 * Await `bootResume`, but never longer than `timeoutMs` and never by throwing.
 * The timer is always cleared, so a boot resume that settles first leaves no
 * dangling handle holding a tab (or a test's event loop) open.
 */
async function awaitBootResume(bootResume: Promise<unknown>, timeoutMs: number): Promise<void> {
  let timer: ReturnType<typeof setTimeout> | undefined;
  try {
    await Promise.race([
      // A rejected boot resume means "no live session" — a valid answer to
      // report, not an error to propagate to the host.
      bootResume.then(
        () => undefined,
        () => undefined,
      ),
      new Promise<void>((resolve) => {
        timer = setTimeout(resolve, timeoutMs);
      }),
    ]);
  } finally {
    if (timer !== undefined) clearTimeout(timer);
  }
}
